<template>
  <div
    v-if="deliveryMethods"
  >
    <div class="text-h6 text-uppercase text-weight-bold q-mb-md">
      {{ $t('deliveryMethods.deliveryMethods') }}
    </div>

    <div class="row q-col-gutter-lg">
      <div v-if="isOnlyOnsite">
        Pöntunin verður afhend til þín á: {{ userName }}
      </div>

      <div
        v-for="deliveryMethod in allowedDeliveryMethods"
        v-else
        :key="deliveryMethod.id"
        class="col-xs-12 col-sm-6 col-md-2"
        @click="selectDeliveryMethod(deliveryMethod)"
      >
        <div
          class="deliveryMethod cursor-pointer text-body1 text-center
          rounded-borders q-py-lg q-px-xl full-height"
          :class="{
            selected: currentDeliveryMethod && currentDeliveryMethod.id === deliveryMethod.id
          }"
        >
          <q-radio
            v-model="selected"
            :val="deliveryMethod.id"
            @input="selectDeliveryMethod(deliveryMethod)"
            @click="selectDeliveryMethod(deliveryMethod)"
          />

          <div class="text-uppercase">
            {{ translateTitle(deliveryMethod) }}
          </div>

          <div>
            {{ currentLocation.getWaitingTime(deliveryMethod.id) }}
            {{ $t('location.waitingTimes.minutes') }}
          </div>

          <div
            v-if="deliveryMethod.priceImpact"
            class="text-weight-bold"
          >
            {{ deliveryMethod.priceImpact }} kr.
          </div>
        </div>
      </div>

      <div
        v-if="showAddressField"
        class="col-xs-12 col-sm-grow"
      >
        <div
          class="deliveryMethod selected row cursor-pointer text-body1 text-center
          rounded-borders q-pa-lg full-height full-width items-center"
        >
          <div
            class="col-xs-12 col-sm-grow"
          >
            <div class="text-body1">
              {{ $t('user.address') }}
            </div>
            <UserAddressSelect
              :value="currentDeliveryMethod.addressId || null"
              @input="updateAddress"
            />
          </div>
          <div class="col-xs-12 col-sm-shrink on-right q-mt-xs-lg text-right">
            <q-btn
              class="q-btn--secondary"
              :size="$q.platform.is.mobile ? 'md' : 'lg'"
              :label="$t('user.addAddress')"
              @click="showAddressDialog = true"
            />
          </div>
        </div>
      </div>
    </div>

    <AuthenticationDialogAddress
      :show="showAddressDialog"
      @submit="saveAddress"
      @close="showAddressDialog = false"
    />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import language from 'quasar-pilot-core/src/mixins/util/language';
import errorDialog from 'quasar-pilot-core/src/mixins/errorDialog';
import vuexUtils from 'quasar-pilot-core/src/mixins/util/vuex';
import { constants } from '../../lib/constants.js';

export default {
  name: 'OrderDeliveryMethods',
  components: {
    AuthenticationDialogAddress: defineAsyncComponent(() => import('components/authentication/dialog/Address')),
    UserAddressSelect: defineAsyncComponent(() => import('components/user/AddressSelect')),
  },

  mixins: [language, errorDialog, vuexUtils],

  data() {
    return {
      deliveryMethods: [],
      showAddressField: false,
      showAddressDialog: false,
      selected: null,
    };
  },

  computed: {
    ...mapState('location', { currentLocation: 'current' }),
    ...mapState('order', { currentDeliveryMethod: 'deliveryMethod', orderProducts: 'products' }),
    ...mapState('user', { userName: 'name', addresses: 'addresses' }),

    isOnlyOnsite() {
      return (this.deliveryMethods.length === 1
        && this.deliveryMethods[0].type === constants.DELIVERY_METHOD_TYPE_ONSITE);
    },

    allowedDeliveryMethods() {
      const allowed = this.deliveryMethods.filter((m) => this.isAllowed(m));
      return allowed;
    },
  },

  created() {
    this.deliveryMethods = this.currentLocation.deliveryMethods;
    const defaultDeliveryMethod = this.deliveryMethods.find((m) => m.id === constants.DEFAULT_DELIVERY_METHOD);
    if (defaultDeliveryMethod) {
      this.selectDeliveryMethod(defaultDeliveryMethod);
    }
    else {
      this.selectDeliveryMethod(this.deliveryMethods[0]);
    }

    if (this.currentDeliveryMethod) {
      this.selected = this.currentDeliveryMethod.id;
      // eslint-disable-next-line max-len
      this.showAddressField = (this.currentDeliveryMethod.type === constants.DELIVERY_METHOD_TYPE_DELIVER);
    }
  },

  methods: {
    ...mapMutations('order', ['updateDeliveryMethod']),
    ...mapActions('order', ['updateTotalPrice']),
    ...mapActions('user', ['saveUserAddress']),

    isAllowed(deliveryMethod) {
      // Check if the delivery method is open for location
      if (!this.currentLocation.isOpen(deliveryMethod.id)) {
        return false;
      }

      let r = true;
      this.orderProducts.forEach((p) => {
        if (!p.deliveryMethods[deliveryMethod.id]) {
          r = false;
        }
      });

      return r;
    },

    selectDeliveryMethod(deliveryMethod) {
      this.updateDeliveryMethod(deliveryMethod);
      this.updateTotalPrice();

      this.selected = deliveryMethod.id;
      this.showAddressField = (deliveryMethod.type === constants.DELIVERY_METHOD_TYPE_DELIVER);
    },

    updateAddress({ id, address, postalCode }) {
      const deliveryMethod = this.unobserve(this.currentDeliveryMethod);
      deliveryMethod.address = address;
      deliveryMethod.postalCode = postalCode;
      deliveryMethod.addressId = id;

      this.updateDeliveryMethod(deliveryMethod);
    },

    async saveAddress({ id, address, postalCode }) {
      this.updateAddress({ id, address, postalCode });
      this.showAddressDialog = false;

      // eslint-disable-next-line max-len
      const hasAddress = this.addresses.filter((a) => (a.address === address && a.postalCode === postalCode));
      // Only save a new address if it hasn't been saved
      if (hasAddress < 1) {
        try {
          await this.saveUserAddress({ address, postalCode });
        }
        catch (e) {
          this.showErrorDialog(e);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.deliveryMethod {
  border: 1px solid $table-border-color;
}

.selected {
  border-color: $primary;
}
</style>
